import capgeminiLogo from '@/assets/brand/capgemini-logo.svg';
import capgeminiSpade from '@/assets/brand/capgemini-spade.svg';
import { cn } from '@/lib/utils';

import { brandMaskFor } from './brandMask';

/**
 * The Capgemini mark, drawn as a CSS mask rather than an `<img>`.
 *
 * The `<img>` this replaces could not be coloured. Both SVGs paint with
 * `currentColor` (FD-18, so one file serves the Blue and White colourways), but
 * `currentColor` inside an externally-referenced image resolves against that
 * image's own document, not the page — so `className="text-foreground"` on an
 * `<img>` did nothing and the mark rendered black in both themes. Masking moves
 * the colour decision to CSS, where the theme can reach it.
 *
 * Sizes are given in the WIDTH of the box, and the box is not the mark: both
 * viewBoxes carry the exclusion-zone guides, so the artwork occupies 85.1% of
 * the logo's box and 60.5% of the spade's. FD-18's 175px minimum is measured on
 * the artwork, which is why `logo` defaults to a 208px box — 177px of actual
 * mark. `contain` keeps the aspect and the exclusion zone intact.
 */
/**
 * Per-mark geometry, derived from the files rather than eyeballed.
 *
 * `inkStartFraction` is where the artwork actually begins inside its own viewBox,
 * as a fraction of the drawn width — the exclusion-zone guides both files carry as
 * padding. Measured from the committed SVGs: the logo's ink starts at x=26.85 of
 * 358.01 (7.5%), the spade's at 29.8 of 150 (19.9%).
 *
 * `flush` computes its offset from these instead of hardcoding one: the previous
 * `-0.98rem` and `-0.3rem` were hand-measured against these exact exports at one
 * size, and would have gone silently stale if either file's padding changed — or
 * simply been wrong wherever a caller set a different size.
 */
const MARKS = {
  logo: { src: capgeminiLogo, inkStartFraction: 26.85 / 358.01 },
  spade: { src: capgeminiSpade, inkStartFraction: 29.8 / 150 },
} as const;

export function BrandMark({
  variant = 'logo',
  flush = false,
  className,
}: {
  variant?: 'logo' | 'spade';
  /**
   * Pull the artwork's own left padding back out, so the ink lines up with text
   * below or beside it instead of sitting visibly indented.
   *
   * Needed because the box is not the mark. `contain` fits the logo by height
   * (a 208x68 box against a 3.057 image aspect leaves 0.06px of horizontal
   * slack), so the ink begins 7.5% of the drawn width in — 15.65px, which is
   * exactly the step you see between the logo and a title left-aligned under it.
   * The spade's guides are proportionally larger, at 19.75% of its box.
   */
  flush?: boolean;
  className?: string;
}) {
  const { src, inkStartFraction } = MARKS[variant];
  const mask = brandMaskFor(src);
  // A percentage `translateX`, which resolves against this element's *own* width,
  // so the shift stays correct at any size a caller sets through `className` — a
  // rem constant would only have been right at the default. Physical, not logical:
  // the padding being cancelled is baked into the artwork's left edge and does not
  // flip with writing direction, so under `dir="rtl"` a logical property would
  // pull from the right, leave the real padding in place, and open a gap opposite.
  const flushShift = flush
    ? { transform: `translateX(-${String(inkStartFraction * 100)}%)` }
    : {};

  return (
    <span
      aria-hidden="true"
      className={cn(
        'block shrink-0 bg-current',
        // 208 x 68 keeps the artwork above FD-18's floor; the spade is square-ish
        // and has no floor of its own, only the rule that the full logo must
        // already have appeared on the page.
        variant === 'logo' ? 'h-[68px] w-[208px]' : 'size-7',
        className,
      )}
      style={{ WebkitMask: mask, mask, ...flushShift }}
    />
  );
}
